import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useReducer
} from 'react';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import fetchAPI from '../../utils/fetchAPI';
import GroupsAndUsersInputBoxView from '../view/GroupsAndUsersInputBoxView';
import { FormBuilderContext } from './FormBuilderEngine';
import { FormBuilderShareContext } from './FormBuilderShareEngine';

const GroupsAndUsersReducer = (state, action) => {
  const { type, payload, id } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    case 'delete_update_autoEnrollGroup':
      delete state.autoEnrollGroups[id];
      return { ...state };
    // case 'update_autoEnrollGroups':
    //   return {
    //     ...state,
    //     autoEnrollGroups: { ...state.autoEnrollGroups, ...payload }
    //   };
    default:
      return state;
  }
};

const initialReducerState = {
  autoEnrollGroups: {}
};

const GroupsAndUsersInputBoxEngine = () => {
  const { groups } = useContext(FormBuilderShareContext);
  const { form, formId } = useContext(FormBuilderContext);
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const { setIsLoading } = useGlobalVariables();
  const [data, dispatch] = useReducer(
    GroupsAndUsersReducer,
    initialReducerState
  );
  const [recipients, setRecipients] = useState([]); // could be in the reducer - reducer added after this was implemented, can be changed and maintaned in reducer if wanted to
  const [
    isBeforeSendWarningDialogOpen,
    setIsBeforeSendWarningDialogOpen
  ] = useState(false);
  const [group2Remove, setGroup2Remove] = useState(null);
  const [isAutoEnrollChecked, setIsAutoEnrollChecked] = useState(false);
  const [listItemInHover, setListItemInHover] = useState('');
  const [inDeleteSequence, setInDeleteSequence] = useState('');
  const [cacheKiller, setCacheKiller] = useState(0);

  const loadOptions = _groups => v => {
    const endPoint = '/users/v1/users';
    const generalURLParams = "offset=0&limit=100&sort='status' asc";
    const queryActiveUsers =
      '[{"field":"\'status\'","op":"=","value":"\'ACTIVE\'"}]';
    let queryUserType = '';
    const allowExternal = form && form.allowExternalUserAccounts;
    if (!allowExternal) {
      queryUserType = `,[{"field":"'schema'", "op": "=", "value": "'internal'"}]`;
    }
    const queryFilterMatches = `[{"field":"'info'->'email'","op":"ILIKE","value":"%${v}%","internOp":"OR"},{"field":"'info'->'lastName'","op":"ILIKE","value":"%${v}%","internOp":"OR"},{"field":"'info'->'userName'","op":"ILIKE","value":"%${v}%","internOp":"OR"},{"field":"'info'->'firstName'","op":"ILIKE","value":"%${v}%","internOp":"OR"}]`;
    const query = `${endPoint}?${generalURLParams}&query=[${queryActiveUsers}${queryUserType},${queryFilterMatches}]`;

    return fetchAPI(query).then(({ results }) => {
      const users = results.map(u => ({
        label: `${u.info.firstName} ${u.info.lastName} (${u.info.email})`,
        value: u.id,
        type: 'user',
        schema: u.schema
      }));
      const groups = _groups
        .filter(g =>
          (g.label || '').toLowerCase().includes((v || '').toLowerCase())
        )
        .filter(g => allowExternal || g.schemaCode !== 'external')
        .map(g => ({
          ...g,
          label: `${g.label} (Group)`,
          type: 'group'
        }));
      return [...users, ...groups];
    });
  };

  const updateAutoEnrolledGroups = useCallback(() => {
    if (!groups || !groups.length) {
      return;
    }

    const queryParam = `[{"field":"'groupType'","op":"=","value":"'enrollment'"}]`;
    fetchAPI(`/forms/v1/forms/${formId}/share/group?query=${queryParam}`).then(
      response => {
        const results = response.results;

        const autoEnrollGroups = results.reduce((accumulator, currentValue) => {
          const group = groups.find(g => g.value === currentValue.groupId);
          if (group) {
            currentValue.label = group.label;
            accumulator[currentValue.id] = currentValue;
          }
          return accumulator;
        }, {});

        dispatch({
          type: 'update',
          payload: { autoEnrollGroups }
        });
      }
    );
  }, [groups]);

  useEffect(() => {
    if (!groups || !groups.length) {
      return;
    }
    updateAutoEnrolledGroups();
  }, [groups]);

  useEffect(() => {
    setCacheKiller(+new Date());
  }, [form]);

  const onRecipientsChange = useCallback(v => {
    setRecipients(v);
  }, []);

  const onEmailsSend = useCallback(() => {
    const groupType = isAutoEnrollChecked ? 'enrollment' : 'invite';

    const body = {
      formSharingList: recipients
        .filter(r => r.type === 'group')
        .map(g => ({ groupId: g.value, groupType })),

      userIds: recipients.filter(r => r.type === 'user').map(u => u.value)
    };

    setIsLoading(true);

    fetchAPI(`/forms/v1/forms/${formId}/share/groups`, { method: 'POST', body })
      .then(() => {
        setRecipients([]);
        if (isAutoEnrollChecked) {
          updateAutoEnrolledGroups();
        }
        openSnackbar('success', 'form-builder.share.send-via-email.snackbar');
      })
      .finally(() => {
        setIsLoading(false);
        setIsAutoEnrollChecked(false);
      });

    setIsBeforeSendWarningDialogOpen(false);
  }, [recipients, formId, updateAutoEnrolledGroups, isAutoEnrollChecked]);

  const deleteAutoEnrollGroup = useCallback(
    _id => {
      const id = _id || group2Remove;
      const groupId = data.autoEnrollGroups[id].groupId;
      setIsLoading(true);
      fetchAPI(`/forms/v1/forms/${formId}/share/group/${groupId}`, {
        method: 'DELETE',
        body: {
          groupType: 'enrollment'
        }
      })
        .then(() => {
          setInDeleteSequence(id);
          setTimeout(() => {
            // let delete animation finish
            openSnackbar(
              'success',
              'form-builder.share.delete-auto-enroll-group.snackbar'
            );
          }, 300);
        })
        .catch(() => {
          openSnackbar('error', 'snackbars.general.error');
        })
        .finally(() => {
          setIsLoading(false);
          setGroup2Remove(null);
        });
    },
    [data, group2Remove]
  );

  const viewProps = {
    loadOptions: loadOptions(groups),
    cacheKiller,
    recipients,
    onRecipientsChange,
    isBeforeSendWarningDialogOpen,
    setIsBeforeSendWarningDialogOpen,
    onEmailsSend,
    isAutoEnrollChecked,
    setIsAutoEnrollChecked,
    showAutoEnrollCheckbox: !!(recipients || []).find(r => r.type === 'group'),
    ...data,
    deleteAutoEnrollGroup,
    listItemInHover,
    setListItemInHover,
    group2Remove,
    setGroup2Remove,
    inDeleteSequence,
    setInDeleteSequence,
    dispatch,
    allowExternalUserAccounts: form.allowExternalUserAccounts
  };

  return <GroupsAndUsersInputBoxView {...viewProps} />;
};

export default GroupsAndUsersInputBoxEngine;
